<script setup>
import { onMounted, computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  // Quando true (rota /brain/sync), dispara sync ao montar.
  autoSync: { type: Boolean, default: false },
});

const store = useStore();
const router = useRouter();
const route = useRoute();

const assistants = computed(() => store.getters['brain/allAssistants']);
const loading = computed(() => store.getters['brain/isLoading']);
const syncing = ref(false);

const runSync = async () => {
  if (syncing.value) return;
  syncing.value = true;
  try {
    const result = await store.dispatch('brain/syncAgentsFromOpenclaw');
    const count =
      (result && (result.count || (Array.isArray(result) ? result.length : 0))) || 0;
    useAlert(
      window.I18n
        ? window.I18n.t('BRAIN.ASSISTANTS.SYNC_SUCCESS', { count })
        : `${count} agent(s) synced.`
    );
  } catch (e) {
    useAlert(
      window.I18n
        ? window.I18n.t('BRAIN.ASSISTANTS.SYNC_ERROR', { error: e.message })
        : `Sync failed: ${e.message}`
    );
  } finally {
    syncing.value = false;
  }
};

const openAssistant = assistant => {
  router.push({
    name: 'pipeline_brain_assistants',
    params: { accountId: route.params.accountId },
    query: { id: assistant.id },
  });
};

onMounted(async () => {
  await store.dispatch('brain/fetchAssistants');
  if (props.autoSync) {
    await runSync();
    router.replace({
      name: 'pipeline_brain_assistants',
      params: { accountId: route.params.accountId },
    });
  }
});
</script>

<template>
  <div class="brain-assistants p-4">
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
        {{ $t('BRAIN.ASSISTANTS.TITLE') }}
      </h1>
      <woot-button
        variant="solid"
        color="blue"
        icon="i-lucide-refresh-cw"
        size="sm"
        :is-loading="syncing"
        @click="runSync"
      >
        {{ $t('BRAIN.ASSISTANTS.SYNC_BUTTON') }}
      </woot-button>
    </div>

    <div v-if="loading" class="text-slate-500 text-sm">
      {{ $t('BRAIN.ASSISTANTS.LOADING') }}
    </div>

    <div
      v-else-if="!assistants.length"
      class="flex flex-col items-center justify-center text-center py-12 px-4 rounded-lg border border-dashed border-slate-300 dark:border-slate-700"
    >
      <span class="i-lucide-bot w-10 h-10 text-slate-400 mb-3" />
      <p class="text-sm text-slate-500 max-w-md">
        {{ $t('BRAIN.ASSISTANTS.EMPTY') }}
      </p>
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="assistant in assistants"
        :key="assistant.id"
        class="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
      >
        <span class="i-lucide-bot w-5 h-5 text-slate-500" />
        <div class="flex-1 min-w-0">
          <p class="font-medium text-slate-800 dark:text-slate-100 text-sm truncate">
            {{ assistant.name }}
          </p>
          <p class="text-xs text-slate-500 truncate">
            {{ assistant.openclaw_agent_slug }}
            <span class="ml-2 inline-flex items-center gap-1">
              <span
                class="w-2 h-2 rounded-full"
                :class="
                  assistant.sync_status === 'synced'
                    ? 'bg-green-500'
                    : 'bg-amber-500'
                "
              />
              {{
                assistant.sync_status === 'synced'
                  ? $t('BRAIN.ASSISTANTS.STATUS_SYNCED')
                  : $t('BRAIN.ASSISTANTS.STATUS_PENDING')
              }}
            </span>
          </p>
        </div>
        <woot-button
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-arrow-right"
          @click="openAssistant(assistant)"
        >
          {{ $t('BRAIN.ASSISTANTS.OPEN') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
